import React, { useState, useMemo, createContext } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import Home from './pages/Home';
import About from './pages/About';
import Projects from './pages/Projects';
import Skills from './pages/Skills';
import Contact from './pages/Contact';
import Workout from './pages/Workout';
import TargetMuscle from './pages/TargetMuscle';
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';

// Create a theme context
export const ColorModeContext = createContext({ 
  toggleColorMode: () => {},
  mode: 'light'
});

function App() {
  const [mode, setMode] = useState('dark');
  
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
      mode,
    }),
    [mode],
  );

  const lightTheme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#2e5077', // Professional blue
        light: '#557ba5',
        dark: '#15304d',
      },
      secondary: {
        main: '#ff6b6b', // Coral accent color
      },
      accent: {
        main: '#ffd166', // Gold accent
        light: '#ffe9a8',
      },
      background: {
        default: '#f8f9fa',
        paper: '#ffffff',
      },
      text: {
        primary: '#1a2a3a', // Dark blue-gray for text
        secondary: '#536b83', // Lighter blue-gray
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '3.5rem',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.1,
      },
      h2: {
        fontSize: '2.25rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
        letterSpacing: '-0.005em',
      },
      h4: {
        fontSize: '1.4rem',
        fontWeight: 600,
      },
      h5: {
        fontSize: '1.1rem',
        fontWeight: 600,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.9rem',
        lineHeight: 1.5,
      },
      code: {
        fontFamily: '"JetBrains Mono", "Roboto Mono", monospace',
        fontSize: '0.85rem',
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    spacing: factor => `${0.8 * factor}rem`,
    shape: {
      borderRadius: 8, // Rounded corners for a modern look
    },
    components: {
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem',
            '@media (min-width:600px)': {
              paddingLeft: '2rem',
              paddingRight: '2rem',
            },
            '@media (min-width:1200px)': {
              maxWidth: '1200px',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            padding: '0.6rem 1.2rem',
            fontWeight: 500,
            textTransform: 'none',
            boxShadow: 'none',
            borderWidth: '1px',
            minWidth: 'auto',
          },
          containedPrimary: {
            background: '#2e5077',
            color: '#ffffff',
            '&:hover': {
              background: '#15304d',
              boxShadow: '0 4px 8px rgba(21, 48, 77, 0.2)',
            },
          },
          outlinedSecondary: {
            borderColor: '#ff6b6b',
            color: '#ff6b6b',
            '&:hover': {
              borderColor: '#ff6b6b',
              background: 'rgba(255, 107, 107, 0.05)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
            borderColor: 'rgba(0, 0, 0, 0.08)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            backgroundColor: 'rgba(46, 80, 119, 0.08)',
            color: '#2e5077',
            fontFamily: '"Inter", "Roboto", sans-serif',
            fontSize: '0.75rem',
            fontWeight: 500,
            letterSpacing: '0.3px',
            padding: '0 0.5rem',
            height: '26px',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 6,
            borderRadius: 3,
          },
          bar: {
            borderRadius: 3,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          root: {
            '& > .MuiGrid-item': {
              paddingTop: '0.8rem',
              paddingBottom: '0.8rem',
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          gutterBottom: {
            marginBottom: '0.8em',
          },
        },
      },
    },
  });

  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#64b5f6', // Lighter blue for dark mode
        light: '#9be7ff',
        dark: '#2286c3',
      },
      secondary: {
        main: '#ff6b6b', // Keeping the coral accent
      },
      accent: {
        main: '#ffd166', 
        light: '#ffe9a8',
      },
      background: {
        default: '#121212',
        paper: '#1e1e1e',
      },
      text: {
        primary: '#e0e0e0',
        secondary: '#b0b0b0',
      },
    },
    typography: lightTheme.typography,
    spacing: lightTheme.spacing,
    shape: lightTheme.shape,
    components: {
      ...lightTheme.components,
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.2)',
            borderColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            backgroundColor: 'rgba(100, 181, 246, 0.15)',
            color: '#64b5f6',
            fontFamily: '"Inter", "Roboto", sans-serif',
            fontSize: '0.75rem',
            fontWeight: 500,
            letterSpacing: '0.3px',
            padding: '0 0.5rem',
            height: '26px',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },
  });

  const theme = mode === 'light' ? lightTheme : darkTheme;

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="about" element={<About />} />
            <Route path="projects" element={<Projects />} />
            <Route path="skills" element={<Skills />} />
            <Route path="contact" element={<Contact />} />
          </Route>
          <Route path="/workout" element={<Workout />} />
          <Route path="/target-muscle" element={<TargetMuscle />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
        </Routes>
      </ThemeProvider>
    </ColorModeContext.Provider> 
  );
}

export default App;
